import React, { useState, useEffect, useMemo } from 'react'
import { Map, useMap } from '@vis.gl/react-google-maps'

import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { DeckProps } from '@deck.gl/core'
import { EventsByLocationItem } from 'api/carrierSafetyEvents'
import { HeatmapLayer } from '@deck.gl/aggregation-layers'

interface ILocationChartProps {
  safetyEventsList?: EventsByLocationItem[] | null
}

const DeckGLOverlay = (props: DeckProps) => {
  const map = useMap()
  const overlay = useMemo(() => new GoogleMapsOverlay(props), [])

  useEffect(() => {
    if (map) {
      overlay.setMap(map)
      return () => overlay.setMap(null)
    }
  }, [map, overlay])

  return null
}

const LocationChart: React.FC<ILocationChartProps> = (props) => {
  const { safetyEventsList } = props
  const map = useMap()
  const [defaultZoom] = useState(4)
  const [defaultCenter] = useState({
    lat: 39.8283,
    lng: -98.5795,
  })

  useEffect(() => {
    if (safetyEventsList && map) {
      const defaultCenter = { lat: 39.8283, lng: -98.5795 }
      map.setCenter(defaultCenter)
      map.setZoom(4)
    }
  }, [safetyEventsList, map])

  const safetyEventsData = useMemo(
    () =>
      safetyEventsList?.map((event) => ({
        position: [event.longitude, event.latitude],
        id: event.id,
        type: event.type,
        weight: 1,
      })) || [],
    [safetyEventsList]
  )

  const layers = useMemo(
    () => [
      new HeatmapLayer({
        data: safetyEventsData,
        id: 'heatmap-layer',
        pickable: false,
        getPosition: (d) => d.position,
        getWeight: (d) => d.weight,
        radiusPixels: 30,
        intensity: 4,
        threshold: 0.01,
        weightsTextureSize: 512,
        debounceTimeout: 300,
        colorRange: [
          [255, 255, 178, 150], //rgb(255, 255, 130)
          [254, 217, 118, 150], //rgb(255, 217, 118)
          [254, 178, 76, 150], // #feb24c
          [253, 141, 60, 150], // #fd8d3c
          [240, 59, 32, 150], // #f03b20
          [189, 0, 38, 150], // #bd0026
        ],
      }),
    ],
    [safetyEventsData]
  )

  return (
    <Map
      defaultZoom={defaultZoom}
      defaultCenter={defaultCenter}
      gestureHandling={'greedy'}
      disableDefaultUI={true}
      clickableIcons={false}
      mapId={'SAFETY_EVENTS_MAP'}
    >
      <DeckGLOverlay layers={layers} />
    </Map>
  )
}

export default LocationChart
